import React, { useState } from 'react';
import KoobInputText from './KoobInputText';

type KoobInputNumberProps = Omit<
  React.ComponentProps<typeof KoobInputText>,
  'value' | 'defaultValue' | 'onChange' | 'type' | 'min' | 'max'
> & {
  /** null / undefined / '' all render an empty field */
  value?: number | string | null;
  /** null when the field is empty — never coerced to 0 */
  onValueChange: (value: number | null) => void;
  /** Digits only, no decimal separator. Defaults to true for a whole-number `step` */
  integer?: boolean;
  /** Left to form validation; min >= 0 only blocks the minus sign */
  min?: number | string;
  /** A non-negative max caps the typed value; a negative one is left to form validation */
  max?: number | string;
  /**
   * Value the parent falls back to when the field is empty (e.g. 0 for an
   * optional margin). It renders as an empty field, so the fallback never
   * shows up as a digit the user has to work around.
   */
  emptyValue?: number;
};

const parse = (text: string): number | null => {
  const parsed = parseFloat(text);
  return Number.isNaN(parsed) ? null : parsed;
};

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// A whole-number step is how type="number" callers rejected decimals.
const isIntegerStep = (step: number | string | undefined) =>
  step != null && Number.isInteger(Number(step));

// "1,234" or "1,234,567": comma-grouped thousands without a decimal part
const GROUPED_THOUSANDS = /^-?\d{1,3}(,\d{3})+$/;

const sanitize = (
  rawText: string,
  integer: boolean,
  allowNegative: boolean,
  pasted: boolean,
) => {
  // A typed "1,234" is a decimal on its way to more digits, but the same text
  // pasted from a spreadsheet is a grouped integer.
  const text =
    pasted && GROUPED_THOUSANDS.test(rawText.trim())
      ? rawText.replace(/,/g, '')
      : rawText;
  const negative = allowNegative && text.trim().startsWith('-');
  // A pasted "1,234.56" or "1.234,56" mixes both separators: only the last
  // one is the decimal point. A lone comma is a typed decimal separator.
  const decimalIndex = Math.max(text.lastIndexOf(','), text.lastIndexOf('.'));
  const isMixed = text.includes(',') && text.includes('.');
  const normalized = isMixed
    ? `${text.slice(0, decimalIndex).replace(/[.,]/g, '')}.${text.slice(decimalIndex + 1)}`
    : text.replace(',', '.');
  let cleaned = normalized.replace(/[^\d.]/g, '');

  if (integer) {
    cleaned = cleaned.split('.')[0];
  } else {
    const [whole, ...decimals] = cleaned.split('.');
    cleaned = decimals.length > 0 ? `${whole}.${decimals.join('')}` : whole;
  }

  // "05" → "5", but keep "0" and "0.5" typable
  cleaned = cleaned.replace(/^0+(?=\d)/, '');

  return negative ? `-${cleaned}` : cleaned;
};

/**
 * Numeric text field. The field can be emptied and retyped freely: the typed
 * text is kept as a draft and the parent only receives a number, or null when
 * the field is empty. Use it instead of `KoobInputText type="number"`, whose
 * value can't be cleared once a handler coerces it with `Number()`.
 */
const KoobInputNumber = React.forwardRef<
  HTMLInputElement,
  KoobInputNumberProps
>(
  (
    {
      value,
      onValueChange,
      integer,
      step,
      min,
      max,
      emptyValue,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const [draft, setDraft] = useState<string | null>(null);

    const current = toNumber(value);
    const isEmpty = current === null || current === emptyValue;
    // The draft wins while it still describes the parent's value, so "1." or
    // "-" survive a re-render; any outside change falls back to the value.
    const typed = draft === null ? null : parse(draft);
    const isDraftCurrent =
      draft !== null && (typed === current || (typed === null && isEmpty));
    const display = isDraftCurrent ? draft : isEmpty ? '' : String(current);

    const isInteger = integer ?? isIntegerStep(step);
    const minimum = toNumber(min);
    const maximum = toNumber(max);
    const allowNegative = minimum === null || minimum < 0;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { inputType } = event.nativeEvent as InputEvent;
      const pasted =
        inputType === 'insertFromPaste' || inputType === 'insertFromDrop';
      const cleaned = sanitize(
        event.target.value,
        isInteger,
        allowNegative,
        pasted,
      );
      const parsed = parse(cleaned);
      // Only a non-negative max can cap mid-entry: "-1" is a valid step
      // towards "-15" under a max of -10.
      const isOverMax =
        maximum !== null && maximum >= 0 && parsed !== null && parsed > maximum;
      const next = isOverMax ? String(maximum) : cleaned;
      setDraft(next);
      onValueChange(parse(next));
    };

    return (
      <KoobInputText
        autoComplete="off"
        {...props}
        ref={ref}
        // JS callers migrating from type="number" may still pass it
        type="text"
        inputMode={allowNegative ? 'text' : isInteger ? 'numeric' : 'decimal'}
        value={display}
        onChange={handleChange}
        // Same as KoobInputStepper: focusing selects the value so typing
        // replaces it instead of appending to it.
        onFocus={(event) => {
          event.currentTarget.select();
          onFocus?.(event);
        }}
        onBlur={(event) => {
          // Normalise "5." to "5" but keep what was typed: an explicit entry of
          // the empty value (e.g. 0) must stay visible, not turn back to empty.
          setDraft(typed === null ? null : String(typed));
          onBlur?.(event);
        }}
      />
    );
  },
);

KoobInputNumber.displayName = 'KoobInputNumber';

export default KoobInputNumber;
